import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import TuneIcon from '@mui/icons-material/Tune';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import CloseIcon from '@mui/icons-material/Close';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { leaveBalanceServices } from '../../services/leaveBalanceServices';
import { useSnackbar } from '../../utils/useSnackbar';
import TableSkeleton from '../common/TableSkeleton';
import EmptyState from '../common/EmptyState';
import PageHeader from '../ui/PageHeader';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';

const CustomNoRowsOverlay = () => (
  <EmptyState
    icon={<AccountBalanceWalletRoundedIcon sx={{ fontSize: 30 }} />}
    title="No Employee Records Found"
    description="No employee leave balances match your search query or criteria."
  />
);

const formatDateTime = (val) => {
  if (!val) return '—';
  return val;
};

const LeaveBalanceManagement = () => {
  const { data: userData } = useSelector((state) => state.userDetails);
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // --- User Balances State ---
  const [balances, setBalances] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const pageSizeOptions = [5, 10, 15, 25];

  const isSearchFirstRender = useRef(true);

  // --- Adjust Dialog State ---
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustError, setAdjustError] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // --- Monthly Credit Config Dialog State ---
  const [openConfigDialog, setOpenConfigDialog] = useState(false);
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [monthlyCreditInput, setMonthlyCreditInput] = useState('1.5');
  const [savingConfig, setSavingConfig] = useState(false);

  // Debounce search
  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch balances
  const fetchBalances = useCallback(async () => {
    try {
      setLoading(true);
      const sortField = sortModel?.[0]?.field || 'id';
      const sortDir = sortModel?.[0]?.sort || 'desc';
      const response = await leaveBalanceServices.getAllBalances(
        debouncedSearch,
        paginationModel.page,
        paginationModel.pageSize,
        sortField,
        sortDir
      );
      setBalances(response?.content || []);
      setRowCount(response?.totalElements || 0);
    } catch (err) {
      console.error('Failed to fetch leave balances:', err);
      showSnackbar('Failed to load leave balances.', 'error');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, paginationModel.page, paginationModel.pageSize, sortModel, showSnackbar]);

  // Fetch config
  const fetchConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const res = await leaveBalanceServices.getConfig();
      setConfig(res);
      setMonthlyCreditInput(String(res?.monthlyCredit ?? 1.5));
    } catch (err) {
      console.error('Failed to fetch leave balance config:', err);
      showSnackbar('Failed to load configuration.', 'error');
    } finally {
      setConfigLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel || []);
  };

  // Open config dialog
  const handleOpenConfig = () => {
    fetchConfig();
    setOpenConfigDialog(true);
  };

  // Open adjust dialog
  const handleOpenAdjust = (row) => {
    setSelectedUser(row);
    setAdjustAmount('');
    setAdjustReason('');
    setAdjustError('');
    setOpenAdjustDialog(true);
  };

  // Submit adjust
  const handleSaveAdjust = async () => {
    const amt = parseFloat(adjustAmount);
    if (isNaN(amt) || amt === 0) {
      setAdjustError('Please enter a valid non-zero number (e.g. +1.5 or -2.0).');
      return;
    }

    try {
      setAdjusting(true);
      const adminName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Admin';
      await leaveBalanceServices.adjustBalance({
        userId: selectedUser.userId,
        userName: selectedUser.userName,
        amount: amt,
        reason: adjustReason.trim() || 'Manual adjustment by admin',
        actionById: userData?.id,
        actionByName: adminName,
      });

      showSnackbar(`Successfully adjusted leave balance for ${selectedUser.userName}.`, 'success');
      setOpenAdjustDialog(false);
      fetchBalances();
    } catch (err) {
      console.error('Failed to adjust balance:', err);
      showSnackbar(err.response?.data || 'Failed to adjust leave balance.', 'error');
    } finally {
      setAdjusting(false);
    }
  };

  // Save config
  const handleSaveConfig = async () => {
    const val = parseFloat(monthlyCreditInput);
    if (isNaN(val) || val < 0) {
      showSnackbar('Please enter a valid positive number for monthly credit.', 'error');
      return;
    }

    try {
      setSavingConfig(true);
      const adminName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'Admin';
      const res = await leaveBalanceServices.updateConfig({
        monthlyCredit: val,
        updatedById: userData?.id,
        updatedByName: adminName,
      });
      setConfig(res);
      showSnackbar('Monthly leave credit rate updated successfully.', 'success');
      setOpenConfigDialog(false);
    } catch (err) {
      console.error('Failed to update config:', err);
      showSnackbar('Failed to update configuration.', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  // Navigate to audit page for user
  const handleViewAudit = (row) => {
    navigate('/home/leave-balance-audit', {
      state: {
        userId: row.userId,
        userName: row.userName,
      },
    });
  };

  const columns = useMemo(() => [
    {
      field: 'userName',
      headerName: 'Employee Name',
      flex: 2.2,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={params.value || 'Unknown'}>
            <span style={{ fontWeight: 600 }}>{params.value || `User #${params.row.userId}`}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      field: 'balance',
      headerName: 'Leave Balance',
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => {
        const bal = params.value ?? 0;
        const isLow = bal <= 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              label={`${bal} ${bal === 1 ? 'day' : 'days'}`}
              size="small"
              color={isLow ? 'default' : 'primary'}
              variant={isLow ? 'outlined' : 'filled'}
              sx={{ fontWeight: 700, fontSize: '0.825rem' }}
            />
          </div>
        );
      },
    },
    {
      field: 'lastCreditedAt',
      headerName: 'Last Auto-Credited',
      flex: 1.8,
      minWidth: 160,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '0.85rem' }}>
            {formatDateTime(params.value)}
          </span>
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 130,
      minWidth: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Tooltip title="Adjust Balance">
            <IconButton
              color="primary"
              size="small"
              className="icon-button"
              onClick={() => handleOpenAdjust(params.row)}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="View Balance History">
            <IconButton
              color="secondary"
              size="small"
              className="icon-button"
              onClick={() => handleViewAudit(params.row)}
            >
              <HistoryRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ], []);

  return (
    <div className="data-table-container">
      {/* Header */}
      <PageHeader title="Leave Balance" />

      {/* Snackbar */}
      {SnackbarComponent}

      {/* Action and Search Bar */}
      <div className="search-and-button">
        <Button
          variant="contained"
          onClick={handleOpenConfig}
          className="add-button"
          startIcon={<SettingsRoundedIcon />}
        >
          Credit Config
        </Button>

        <div className="search-container">
          <input
            type="text"
            placeholder="Search Employee..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table">
        <div className="data-grid-container">
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={4} />
            </Box>
          ) : (
            <DataGrid
              rows={balances}
              columns={columns}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={setPaginationModel}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
            />
          )}
        </div>
      </div>

      {/* MONTHLY CREDIT CONFIG DIALOG */}
      <Dialog
        open={openConfigDialog}
        onClose={() => !savingConfig && setOpenConfigDialog(false)}
        disableRestoreFocus
        slotProps={{ paper: { className: 'modern-dialog' } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="modern-dialog-title">
          <span className="modern-dialog-title-text">
            Monthly Balance Credit Configuration
          </span>
          <IconButton
            aria-label="close"
            onClick={() => setOpenConfigDialog(false)}
            disabled={savingConfig}
            className="modern-dialog-close-btn"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modern-dialog-content">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.5 }}>
              Configure the number of leave days credited to every active user on the 1st of each month.
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'rgba(248, 250, 252, 0.95)',
                border: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>
                  Monthly Credit Rate
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={monthlyCreditInput}
                  onChange={(e) => setMonthlyCreditInput(e.target.value)}
                  placeholder="1.5"
                  inputProps={{ step: '0.25', min: '0', style: { textAlign: 'right', fontWeight: 600 } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ '& p': { fontSize: '0.8rem', fontWeight: 500, color: '#64748b' } }}>
                        days / month
                      </InputAdornment>
                    ),
                  }}
                  disabled={configLoading || savingConfig}
                  sx={{
                    width: 170,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>
            </Box>

            {config?.updatedAt && (
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.3 }}>
                  Last updated by: <strong style={{ color: '#334155' }}>{config.updatedByName || 'Admin'}</strong>
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                  Last updated on: <strong style={{ color: '#334155' }}>{formatDateTime(config.updatedAt)}</strong>
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions className="modern-dialog-actions">
          <Button
            onClick={() => setOpenConfigDialog(false)}
            variant="outlined"
            color="inherit"
            disabled={savingConfig}
            sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveConfig}
            disabled={savingConfig || configLoading}
            variant="contained"
            className="add-button"
          >
            {savingConfig ? 'Saving...' : 'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADJUST BALANCE DIALOG */}
      <Dialog
        open={openAdjustDialog}
        onClose={() => !adjusting && setOpenAdjustDialog(false)}
        disableRestoreFocus
        slotProps={{ paper: { className: 'modern-dialog' } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="modern-dialog-title">
          <span className="modern-dialog-title-text">
            Adjust Leave Balance
          </span>
          <IconButton
            aria-label="close"
            onClick={() => setOpenAdjustDialog(false)}
            disabled={adjusting}
            className="modern-dialog-close-btn"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modern-dialog-content">
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              px: 2,
              borderRadius: 1,
              bgcolor: 'rgba(4, 88, 100, 0.05)',
              border: '1px solid rgba(4, 88, 100, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: 'rgba(4, 88, 100, 0.12)',
                  color: 'var(--primary)',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                }}
              >
                {(selectedUser?.userName || 'U')[0].toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2, noWrap: true }}
                >
                  {selectedUser?.userName || `User #${selectedUser?.userId}`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Employee
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
                Current Balance
              </Typography>
              <Chip
                label={`${selectedUser?.balance ?? 0} ${selectedUser?.balance === 1 ? 'day' : 'days'}`}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, height: 22, fontSize: '0.75rem', mt: 0.2 }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Adjustment Amount (Days)"
              type="number"
              size="small"
              fullWidth
              value={adjustAmount}
              onChange={(e) => {
                setAdjustAmount(e.target.value);
                setAdjustError('');
              }}
              placeholder="e.g. +1.5 or -2.0"
              inputProps={{ step: '0.5' }}
              helperText={
                adjustError
                  ? adjustError
                  : adjustAmount && !isNaN(parseFloat(adjustAmount))
                  ? `New balance will be ${(
                      Number(selectedUser?.balance || 0) + parseFloat(adjustAmount)
                    ).toFixed(2).replace(/\\.00$/, '')} days`
                  : 'Enter positive value to add, negative value to deduct.'
              }
              error={Boolean(adjustError)}
              disabled={adjusting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ '& p': { fontSize: '0.8rem', fontWeight: 500, color: '#64748b' } }}>
                    days
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Reason / Remarks"
              multiline
              rows={2}
              size="small"
              fullWidth
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              placeholder="e.g. Special bonus leave, policy correction..."
              disabled={adjusting}
            />
          </Box>
        </DialogContent>

        <DialogActions className="modern-dialog-actions">
          <Button
            onClick={() => setOpenAdjustDialog(false)}
            variant="outlined"
            color="inherit"
            disabled={adjusting}
            sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAdjust}
            disabled={adjusting}
            variant="contained"
            className="add-button"
          >
            {adjusting ? 'Adjusting...' : 'Save Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LeaveBalanceManagement;
